// in both locations ./src/constants/hostRewards.js & ./functions/constants/hostRewards.js
// -------------------------------------------------------------
// HOST REWARD PROGRAM
//
// * ACTIVE MODEL (2026-07-16) - CRIT POT-SHARE *
//   Hosts earn MINTED Crits per round hosted, scaled to the lobby they ran:
//     matchPot   = (paying players in that match) x critEntryCost   (in Crits)
//     base       = matchPot x potPct (20%)
//     reward     = clamp( round(base x depthMultiplier), floor 1, cap 25 )
//   Minted play currency (never skimmed from the prize pool). Crits have no cash
//   value. Depth multiplier rewards hosts who run MORE rounds of the same event
//   (1st x1.0 ... 5th+ x2.0). Self-selected hosts ONLY. GT7 = 15 racers (host is
//   the 16th seat, never counted in their own pot) — EXCEPT hosted-racer events
//   (hostRaces: true), where the host pays for a racer seat and that paid seat
//   IS in the pot like everyone else's.
//   Config: HOST_CRIT_REWARD_CONFIG. Compute: computeHostRoundReward().
//   The minting itself lives server-side in functions/modules/triggers.js.
//
// -- DORMANT MODEL (LEGACY CASH RAKE, do NOT ship) ----------------------------
//   HOST_REWARD_CONFIG + getHostTier/buildHostRewardTable/calculateMatchHostPay
//   are the pre-pivot cash economy - kept intact but UNUSED. The Host Program UI
//   gates all of it behind HOST_CASH_TIERS=false.
// -------------------------------------------------------------

// ============================================================================
// * ACTIVE - CRIT POT-SHARE CONFIG + COMPUTE *  (mirror of functions/constants)
// ============================================================================

export const HOST_CRIT_REWARD_CONFIG = {
  potPct: 0.20,      // share of the per-match lobby pot, minted as Crits
  floor: 1,          // every hosted round pays at least 1 Crit
  cap: 25,           // hard ceiling per round (applied AFTER depth)
  depthStep: 0.25,   // +25% per additional round hosted in the SAME event
  depthCap: 2.0,     // depth multiplier tops out here (5th round onward)
};

/**
 * Depth multiplier for the Nth round a host runs in a single event.
 * ordinal 1 -> 1.0x, 2 -> 1.25x, 3 -> 1.5x, 4 -> 1.75x, 5+ -> 2.0x (capped).
 */
export function hostDepthMultiplier(ordinal) {
  const n = Math.max(1, Math.floor(Number(ordinal) || 1));
  const { depthStep, depthCap } = HOST_CRIT_REWARD_CONFIG;
  return Math.min(depthCap, 1 + depthStep * (n - 1));
}

/**
 * Compute the minted Crit reward for one hosted round (mirror of backend).
 * @returns {{ crits, matchPot, base, depthMultiplier, ordinal }}
 */
export function computeHostRoundReward({ entryCrits, playersInMatch, ordinal = 1 } = {}) {
  const entry = Math.max(0, Math.floor(Number(entryCrits) || 0));
  const players = Math.max(0, Math.floor(Number(playersInMatch) || 0));
  const ord = Math.max(1, Math.floor(Number(ordinal) || 1));

  const { potPct, floor, cap } = HOST_CRIT_REWARD_CONFIG;
  const matchPot = entry * players;
  const base = matchPot * potPct;
  const mult = hostDepthMultiplier(ord);
  const crits = Math.min(cap, Math.max(floor, Math.round(base * mult)));

  return { crits, matchPot, base: Math.round(base), depthMultiplier: mult, ordinal: ord };
}

// ============================================================================
// -- DORMANT - LEGACY CASH RAKE MODEL (unused; UI gated off) --
// ============================================================================

export const HOST_REWARD_CONFIG = {
  // Economics
  rakePct: 0.10,              // 10% total rake
  hostRakeShare: 0.29,        // 29% of rake goes to host ceiling (4% base + 25% bonus combined)
  platformShare: 0.71,        // 71% of rake for platform

  // Payout mode = CASH (real money, withdrawable)
  mode: 'cash',

  // Minimum cash-out threshold
  minCashOutCents: 1000,       // $10.00 minimum

  // Tier thresholds with RPG-style multiplier curve
  // Big jumps early, diminishing returns at top
  // Every match hosted = 1 point. Tier 10 = 2,250 matches.
  tierThresholds: [
    { tier: 1,  points: 0,    name: 'Host I',    multiplier: 0.30, color: '#CD7F32' },
    { tier: 2,  points: 50,   name: 'Host II',   multiplier: 0.45, color: '#C0C0C0' },
    { tier: 3,  points: 150,  name: 'Host III',  multiplier: 0.57, color: '#FFD700' },
    { tier: 4,  points: 300,  name: 'Host IV',   multiplier: 0.67, color: '#00CED1' },
    { tier: 5,  points: 500,  name: 'Host V',    multiplier: 0.75, color: '#9370DB' },
    { tier: 6,  points: 750,  name: 'Host VI',   multiplier: 0.82, color: '#FF6347' },
    { tier: 7,  points: 1050, name: 'Host VII',  multiplier: 0.88, color: '#32CD32' },
    { tier: 8,  points: 1400, name: 'Host VIII', multiplier: 0.93, color: '#FF1493' },
    { tier: 9,  points: 1800, name: 'Host IX',   multiplier: 0.97, color: '#00BFFF' },
    { tier: 10, points: 2250, name: 'Host X',    multiplier: 1.00, color: '#FFD700' },
  ],
};

/**
 * Get host tier information based on accumulated Host Points
 *
 * @param {number} hostPoints - Total Host Points accumulated
 * @returns {Object} { tier, name, multiplier, color, pointsToNext, nextTier, currentPoints }
 */
export function getHostTier(hostPoints = 0) {
  const tiers = HOST_REWARD_CONFIG.tierThresholds;

  // Find the highest tier the host qualifies for
  let currentTier = tiers[0];
  for (let i = tiers.length - 1; i >= 0; i--) {
    if (hostPoints >= tiers[i].points) {
      currentTier = tiers[i];
      break;
    }
  }

  // Calculate points needed for next tier
  const nextTier = tiers.find(t => t.tier === currentTier.tier + 1);
  const pointsToNext = nextTier ? nextTier.points - hostPoints : 0;

  return {
    tier: currentTier.tier,
    name: currentTier.name,
    multiplier: currentTier.multiplier,
    color: currentTier.color,
    pointsToNext,
    nextTier: nextTier ? nextTier.name : null,
    currentPoints: hostPoints,
    // Legacy compat — some UI may still reference 'weight'
    weight: currentTier.multiplier,
  };
}

/**
 * Calculate Host Points earned for hosting a match.
 * V3: FLAT — every match = 1 point. No round weighting.
 *
 * @param {number} _roundNumber - unused, kept for call-site compat
 * @param {number} _totalRounds - unused, kept for call-site compat
 * @returns {number} Host Points earned (always 1)
 */
export function calculateHostPoints(_roundNumber, _totalRounds) {
  return 1;
}

/**
 * Calculate host pay for a single match (the core V3 formula).
 *
 * Formula: matchPool x 10% rake x 29% hostShare x tierMultiplier
 *
 * @param {number} entryFee     - entry fee in DOLLARS
 * @param {number} gridSize     - players per match (e.g. 15 for GT7, 2 for 1v1 bracket)
 * @param {number} tierMultiplier - 0.30 (Tier 1) through 1.00 (Tier 10)
 * @returns {Object} { matchPool, matchRake, hostCeiling, hostPay, hostPayCents }
 */
export function calculateMatchHostPay(entryFee, gridSize, tierMultiplier = 0.30) {
  const fee = Number(entryFee || 0);
  const size = Number(gridSize || 2);
  const mult = Math.max(0, Math.min(1, Number(tierMultiplier || 0.30)));

  const matchPool = fee * size;
  const matchRake = matchPool * HOST_REWARD_CONFIG.rakePct;
  const hostCeiling = matchRake * HOST_REWARD_CONFIG.hostRakeShare;
  const hostPay = hostCeiling * mult;
  const hostPayCents = Math.round(hostPay * 100);

  return {
    matchPool: +matchPool.toFixed(2),
    matchRake: +matchRake.toFixed(2),
    hostCeiling: +hostCeiling.toFixed(2),
    hostPay: +hostPay.toFixed(2),
    hostPayCents,
  };
}

// ============================================================================
// MATCH COUNTING HELPERS
// ============================================================================

/**
 * Compute matches-per-round for each supported event type.
 *
 * @param {Object} E  event facts
 * @returns {Object} { rounds, matchesByRound, canEarnHostRewards }
 */
function computeMatchesByRound(E) {
  const type = (E?.type || 'bracket').toLowerCase();

  // H2H (single match) — NO HOST REWARDS
  if (type === 'h2h') {
    return {
      rounds: 1,
      matchesByRound: [1],
      canEarnHostRewards: false,
    };
  }

  // Showdown (single race) — CAN EARN
  if (type === 'showdown') {
    return {
      rounds: 1,
      matchesByRound: [1],
      canEarnHostRewards: true,
    };
  }

  // Heats (multi-round racing) — CAN EARN
  if (type === 'heats') {
    if (Array.isArray(E.heatsPerRound) && E.heatsPerRound.length > 0) {
      return {
        rounds: E.heatsPerRound.length,
        matchesByRound: E.heatsPerRound.slice(),
        canEarnHostRewards: true,
      };
    }

    // Fallback: halve number of heats until 1
    const startHeats = Math.max(1, Math.floor((E.entrants || 0) / Math.max(1, E.gridSize || 12)));
    const matches = [];
    let h = Math.max(1, startHeats);
    while (h > 1) {
      matches.push(h);
      h = Math.max(1, Math.floor(h / 2));
    }
    matches.push(1);
    return {
      rounds: matches.length,
      matchesByRound: matches,
      canEarnHostRewards: true,
    };
  }

  // Brackets (default) — CAN EARN
  const entrants = Math.max(2, Number(E.entrants || 0));
  const tpm = Math.max(2, Number(E.teamsPerMatch || 2));

  let totalRounds = E.totalRounds || Math.ceil(Math.log2(entrants / tpm)) + 1;
  totalRounds = Math.max(totalRounds, 1);

  const matches = [];
  let m = Math.max(1, Math.floor(entrants / tpm));

  for (let r = 0; r < totalRounds; r++) {
    matches.push(m);
    if (m <= 1) break;
    m = Math.max(1, Math.floor(m / 2));
  }

  while (matches.length < totalRounds) matches.push(1);
  if (matches.length > totalRounds) matches.splice(totalRounds);

  return {
    rounds: totalRounds,
    matchesByRound: matches,
    canEarnHostRewards: true,
  };
}

/**
 * Build the per-match host reward table for ANY event shape.
 *
 * V3: Each match is economically independent.
 * Host pay per match = matchPool x 10% x 29% x tierMultiplier.
 * No pooling, no weight-based sharing.
 *
 * @param {Object} E  event facts
 * @param {Object} CFG HOST_REWARD_CONFIG
 *
 * @returns {Object} {
 *   gross, rake, hostCeiling, platformRemainder,
 *   rounds, matchesByRound,
 *   perMatchHostPay,         // Object from calculateMatchHostPay (at Tier 10 ceiling)
 *   totalMatches,
 *   canEarnHostRewards,
 *   mode, minCashOutCents
 * }
 */
export function buildHostRewardTable(E, CFG = HOST_REWARD_CONFIG) {
  const entryFee = Number(E.entryFee || 0);
  const entrants = Number(E.entrants || 0);
  const rakePct = Number(CFG.rakePct);
  const hostRakeShare = Number(CFG.hostRakeShare);

  // Gross pool and rake
  const gross = +(entryFee * entrants).toFixed(2);
  const rake = +(gross * rakePct).toFixed(2);
  const hostCeiling = +(rake * hostRakeShare).toFixed(2);
  const platformRemainder = +(rake - hostCeiling).toFixed(2);

  // Determine round structure
  const { rounds, matchesByRound, canEarnHostRewards } = computeMatchesByRound(E);

  // Total matches
  const totalMatches = matchesByRound.reduce((sum, m) => sum + m, 0);

  // Per-match economics (at the match level, not event level)
  // For racing/showdown: gridSize is the match player count
  // For brackets: 2 players per match (1v1)
  const matchGridSize = E.gridSize || (E.type === 'bracket' ? 2 : 2);
  const perMatchHostPay = calculateMatchHostPay(entryFee, matchGridSize, 1.0); // Tier 10 ceiling

  return {
    gross,
    rake,
    hostCeiling,
    platformRemainder,
    rounds,
    matchesByRound,
    perMatchHostPay,
    totalMatches,
    canEarnHostRewards,
    mode: CFG.mode,
    minCashOutCents: CFG.minCashOutCents,
  };
}

/**
 * Calculate total host earnings for an event given tier and match count.
 *
 * @param {Object} table      - result of buildHostRewardTable()
 * @param {number} matchCount - number of matches this host will host
 * @param {number} tierMultiplier - host's tier multiplier (0.30 to 1.00)
 * @returns {Object} { perMatchPay, totalPay, totalPayCents, matchCount }
 */
export function calculateHostEventEarnings(table, matchCount, tierMultiplier = 0.30) {
  if (!table.canEarnHostRewards || matchCount <= 0) {
    return { perMatchPay: 0, totalPay: 0, totalPayCents: 0, matchCount: 0 };
  }

  const perMatchPay = +(table.perMatchHostPay.hostCeiling * tierMultiplier).toFixed(2);
  const totalPay = +(perMatchPay * matchCount).toFixed(2);
  const totalPayCents = Math.round(totalPay * 100);

  return {
    perMatchPay,
    totalPay,
    totalPayCents,
    matchCount,
  };
}

/**
 * Format currency for display
 */
export function formatHostReward(amount) {
  return `$${Number(amount).toFixed(2)}`;
}

/**
 * Validate if host can cash out
 *
 * @param {number} pendingCents - pending credits in cents
 * @param {number} minCents - minimum threshold in cents
 * @returns {Object} { canCashOut, message }
 */
export function canCashOut(pendingCents, minCents = HOST_REWARD_CONFIG.minCashOutCents) {
  if (pendingCents >= minCents) {
    return {
      canCashOut: true,
      message: `You can claim ${formatHostReward(pendingCents / 100)}`,
    };
  }

  const needed = minCents - pendingCents;
  return {
    canCashOut: false,
    message: `Need ${formatHostReward(needed / 100)} more to reach minimum ($${(minCents / 100).toFixed(2)})`,
  };
}

// ============================================================================
// EXAMPLES & REFERENCE
// ============================================================================

// Showdown (GT7, 15 racers, $25 entry):
//   matchPool = 15 x $25 = $375
//   matchRake = $37.50
//   hostCeiling (29%) = $10.88
//   Tier 1  (30%) = $3.26
//   Tier 10 (100%) = $10.88

// 12-round racing event (GT7, $25 entry):
//   Each match: same $10.88 ceiling
//   Host 12 rounds x 1 match each = $10.88 x 12 x tierMultiplier
//   Tier 10: $130.56 total for the event

// Bracket 1v1 ($25 entry):
//   matchPool = 2 x $25 = $50
//   matchRake = $5.00
//   hostCeiling (29%) = $1.45
//   Tier 1: $0.44
//   Tier 10: $1.45

export const EXAMPLE_SHOWDOWN_GT7 = {
  type: 'showdown',
  entrants: 15,
  entryFee: 25,
  gridSize: 15,
};

export const EXAMPLE_32_BRACKET = {
  type: 'bracket',
  entrants: 32,
  entryFee: 5,
  teamsPerMatch: 2,
};

export const EXAMPLE_H2H = {
  type: 'h2h',
  entrants: 2,
  entryFee: 5,
};